/*
 * This component will take a criteria object for a currency or a relationship,
 * and will return a string in a span.
 */

import * as React from 'react';
import {connect} from 'react-redux';

import {Collections} from 'nutshell-core/collections';
import * as Leads from 'nutshell-core/leads';
import * as Schema from 'nutshell-core/schema';

import type {Comparator} from 'nutshell-graphql-types';
import type {EntityFilterFieldConfig as EntityFilterFieldConfigFragment} from 'nutshell-graphql-types';

import type {NutshellState} from '../../../../store';

import {getCollectionNameFromFieldName} from './get-collection-name-from-field-name';

type Collection = {
    [collectionModelId: string]: any;
}; // Will be a Collections.CollectionModel

type OwnProps = {
    criteria: EntityFilterFieldConfigFragment;
};
type StateProps = {
    schema: Schema.Properties;
    collection: Collection;
};
type Props = OwnProps & StateProps;

function mapStateToProps(state: NutshellState, ownProps: OwnProps): StateProps {
    const schema = Leads.getLeadsSchemaPropertyMap(state);
    const collectionName = getCollectionNameFromFieldName(ownProps.criteria.fieldName, schema);

    return {
        schema,
        collection: collectionName ? Collections.getAllOfType(state, collectionName) : {},
    };
}

export class ReadableCriteriaComponent extends React.PureComponent<Props> {
    render() {
        const criteriaString = stringifyCriteria(
            this.props.criteria,
            this.props.schema,
            this.props.collection
        );

        if (!criteriaString) {
            const supportLink = (
                <a
                    href='mailto:support@nutshell.com'
                    target='_blank'
                    rel='noopener noreferrer'
                    style={{textDecoration: 'underline'}}
                >
                    contact support
                </a>
            );

            return <span>Trouble getting condition. Please {supportLink}.</span>;
        }

        return <span>{criteriaString}</span>;
    }
}

// @ts-expect-error
const connector = connect<Props, OwnProps, _, _, _, _>(mapStateToProps);
export const ReadableCriteria = connector(ReadableCriteriaComponent);

/*
 * Given a critera object of either a currency or a relationship, return a string
 * explaining the criteria in a simple phrase.
 */
function stringifyCriteria(
    criteria: EntityFilterFieldConfigFragment,
    schema: Schema.Properties,
    collection: Collection
): string {
    switch (criteria.__typename) {
        case 'EntityFilterFieldConfigCurrency':
            return stringifyCurrencyCriteria(criteria);
        case 'EntityFilterFieldConfigRelationship':
            return stringifySingleRelationshipCriteria(criteria, schema, collection);
        default:
            return '';
    }
}

/*
 * Given a criteria object for a currency field, return a string that we can
 * show to the user.
 */
// @ts-expect-error
function stringifyCurrencyCriteria(criteria): string {
    // No union types from graphql, unfortunately, so we need to check these but
    // they're already safe based on the `fieldType`
    const {currencyCode, currencyValue, currencyComparator} = criteria;

    if (
        !currencyComparator ||
        typeof currencyValue === 'undefined' ||
        currencyValue === null ||
        !currencyCode
    ) {
        return '';
    }

    const comparator = stringifyCurrencyComparator(currencyComparator);
    const value = Number(currencyValue).toLocaleString(undefined, {
        style: 'currency',
        currency: currencyCode,
    });

    return `Value ${comparator} ${value}`;
}

/*
 * Return a comparator symbol rather than our string constants.
 */
function stringifyCurrencyComparator(comparator: Comparator): string {
    switch (comparator) {
        case 'GREATER':
            return '>';
        case 'LESS':
            return '<';
        case 'EQUAL':
        default:
            return '=';
    }
}

/*
 * Given a relationship critiera object (along with a schema and collection object of the correct type),
 * return a string that we can show to the user.
 */
function stringifySingleRelationshipCriteria(
    // @ts-expect-error
    criteria,
    schema: Schema.Properties,
    collection: Collection
) {
    if (!criteria.relationshipData) return '';

    const valueId = criteria.relationshipData[0];

    // Shouldn't ever happen, really
    if (!valueId || !collection[valueId]) return '';

    const field = schema[criteria.fieldName];
    // @ts-expect-error
    const verb = field.isAndable ? 'include' : 'is';

    // @ts-expect-error
    return `${field.title} ${verb} '${collection[valueId].name}'`;
}
